import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AppLayout from '../components/AppLayout';
import toast from 'react-hot-toast';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  FolderIcon,
  FolderOpenIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';

interface SiteCategory {
  _id: string;
  level: 'main' | 'sub' | 'detail';
  name: string;
  parentName?: string;
  grandParentName?: string;
  isActive: boolean;
  sortOrder: number;
}

interface Stats {
  main: number;
  sub: number;
  detail: number;
  total: number;
}

const TicimaxCategoryManagement: React.FC = () => {
  const [categories, setCategories] = useState<SiteCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<SiteCategory | null>(null);
  const [stats, setStats] = useState<Stats>({ main: 0, sub: 0, detail: 0, total: 0 });
  const [formData, setFormData] = useState({
    level: 'main' as 'main' | 'sub' | 'detail',
    name: '',
    parentName: '',
    grandParentName: '',
    sortOrder: 0
  });

  // Excel import için
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    loadCategories();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/site-categories');
      setCategories(response.data);
    } catch (error) {
      toast.error('Kategoriler yüklenemedi!');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get('/site-categories/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Stats yüklenemedi:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validasyon
    if (!formData.name.trim()) {
      toast.error('Kategori adı zorunludur!');
      return;
    }

    if (formData.level === 'sub' && !formData.parentName) {
      toast.error('Alt kategori için ana kategori seçimi zorunludur!');
      return;
    }

    if (formData.level === 'detail') {
      if (!formData.grandParentName) {
        toast.error('Detay kategori için ana kategori seçimi zorunludur!');
        return;
      }
      if (!formData.parentName) {
        toast.error('Detay kategori için alt kategori seçimi zorunludur!');
        return;
      }
    }

    try {
      const payload = {
        level: formData.level,
        name: formData.name.trim(),
        parentName: formData.level === 'sub' ? formData.parentName : 
                   formData.level === 'detail' ? formData.parentName : undefined,
        grandParentName: formData.level === 'detail' ? formData.grandParentName : undefined,
        sortOrder: formData.sortOrder
      };

      if (editingCategory) {
        await axios.put(`/site-categories/${editingCategory._id}`, payload);
        toast.success('Kategori güncellendi!');
      } else {
        await axios.post('/site-categories', payload);
        toast.success('Kategori oluşturuldu!');
      }
      
      setShowModal(false);
      setEditingCategory(null);
      resetForm();
      loadCategories();
      loadStats();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız!');
    }
  };

  const handleDelete = async (categoryId: string) => {
    if (window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) {
      try {
        await axios.delete(`/site-categories/${categoryId}`);
        toast.success('Kategori silindi!');
        loadCategories();
        loadStats();
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Kategori silinemedi!');
      }
    }
  };

  const handleEdit = (category: SiteCategory) => {
    setEditingCategory(category);
    setFormData({
      level: category.level,
      name: category.name,
      parentName: category.parentName || '',
      grandParentName: category.grandParentName || '',
      sortOrder: category.sortOrder
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      level: 'main',
      name: '',
      parentName: '',
      grandParentName: '',
      sortOrder: 0
    });
  };

  const getLevelLabel = (level: string) => {
    switch (level) {
      case 'main': return 'Ana Kategori';
      case 'sub': return 'Alt Kategori';
      case 'detail': return 'Detay Kategori';
      default: return level;
    }
  };

  // Ana kategorileri filtrele
  const mainCategories = categories.filter(c => c.level === 'main' && c.isActive);
  
  // Seçili ana kategoriye göre alt kategorileri filtrele
  const getSubCategoriesForMain = (mainName: string) => {
    return categories.filter(c => c.level === 'sub' && c.parentName === mainName && c.isActive);
  };

  // Form için alt kategoriler (grandParentName'e göre)
  const filteredSubCategories = formData.grandParentName 
    ? getSubCategoriesForMain(formData.grandParentName)
    : [];

  // Excel import handler
  const handleExcelImport = async () => {
    if (!importFile) {
      toast.error('Lütfen bir dosya seçin!');
      return;
    }

    setImporting(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', importFile);

    try {
      const response = await axios.post('/site-categories/import-excel', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`${response.data.imported} kategori başarıyla import edildi!`);
      setShowImportModal(false);
      setImportFile(null);
      loadCategories();
      loadStats();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Import başarısız!');
    } finally {
      setImporting(false);
    }
  };

  return (
    <AppLayout title="Ticimax Kategori Yönetimi" subtitle="Site Ana Kategori, Alt Kategori ve Detay Kategori Yönetimi">
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-sm hover-lift">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center">
                <FolderIcon className="w-6 h-6 text-teal-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.main}</p>
            <p className="text-sm text-gray-500">Ana Kategori</p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-sm hover-lift">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center">
                <FolderOpenIcon className="w-6 h-6 text-cyan-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.sub}</p>
            <p className="text-sm text-gray-500">Alt Kategori</p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-sm hover-lift">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <FolderIcon className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.detail}</p>
            <p className="text-sm text-gray-500">Detay Kategori</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm hover-lift">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                <FolderIcon className="w-6 h-6 text-gray-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-sm text-gray-500">Toplam</p>
          </div>
        </div>

        {/* Excel Import Button */}
        <div className="mb-6 flex justify-end">
          <button
            onClick={() => setShowImportModal(true)}
            className="px-4 py-2 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 hover:shadow-lg transition-all duration-300 flex items-center"
          >
            <ArrowUpTrayIcon className="w-5 h-5 mr-2" />
            Excel'den Import
          </button>
        </div>

        {/* Ana Kategori Tablosu */}
        <div className="bg-white rounded-2xl shadow-sm mb-6">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                <FolderIcon className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Ana Kategori</h3>
                <p className="text-sm text-gray-500">{stats.main} kategori</p>
              </div>
            </div>
            <button
              onClick={() => {
                setEditingCategory(null);
                resetForm();
                setFormData(prev => ({ ...prev, level: 'main' }));
                setShowModal(true);
              }}
              className="px-4 py-2 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 hover:shadow-lg transition-all duration-300 flex items-center"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Yeni Ana Kategori
            </button>
          </div>
          
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
              </div>
            ) : categories.filter(c => c.level === 'main').length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Henüz ana kategori eklenmemiş
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">İsim</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sıra</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {categories
                    .filter(c => c.level === 'main')
                    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
                    .map(cat => (
                      <tr key={cat._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900">{cat.name}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-500">{cat.sortOrder}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${cat.isActive ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                            {cat.isActive ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleEdit(cat)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            <PencilIcon className="w-5 h-5 inline" />
                          </button>
                          <button
                            onClick={() => handleDelete(cat._id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="w-5 h-5 inline" />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Alt Kategori Tablosu */}
        <div className="bg-white rounded-2xl shadow-sm mb-6">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                <FolderOpenIcon className="w-6 h-6 text-cyan-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Alt Kategori</h3>
                <p className="text-sm text-gray-500">{stats.sub} kategori</p>
              </div>
            </div>
            <button
              onClick={() => {
                setEditingCategory(null);
                resetForm();
                setFormData(prev => ({ ...prev, level: 'sub' }));
                setShowModal(true);
              }}
              className="px-4 py-2 bg-cyan-600 text-white font-medium rounded-lg hover:bg-cyan-700 hover:shadow-lg transition-all duration-300 flex items-center"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Yeni Alt Kategori
            </button>
          </div>
          
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
              </div>
            ) : categories.filter(c => c.level === 'sub').length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Henüz alt kategori eklenmemiş
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">İsim</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ana Kategori</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sıra</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {categories
                    .filter(c => c.level === 'sub')
                    .sort((a, b) => (a.parentName || '').localeCompare(b.parentName || '') || a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
                    .map(cat => (
                      <tr key={cat._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900">{cat.name}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-teal-100 text-teal-700">
                            {cat.parentName || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-500">{cat.sortOrder}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${cat.isActive ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                            {cat.isActive ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleEdit(cat)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            <PencilIcon className="w-5 h-5 inline" />
                          </button>
                          <button
                            onClick={() => handleDelete(cat._id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="w-5 h-5 inline" />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Detay Kategori Tablosu */}
        <div className="bg-white rounded-2xl shadow-sm">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <FolderIcon className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Detay Kategori</h3>
                <p className="text-sm text-gray-500">{stats.detail} kategori</p>
              </div>
            </div>
            <button
              onClick={() => {
                setEditingCategory(null);
                resetForm();
                setFormData(prev => ({ ...prev, level: 'detail' }));
                setShowModal(true);
              }}
              className="px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 hover:shadow-lg transition-all duration-300 flex items-center"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Yeni Detay Kategori
            </button>
          </div>
          
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
              </div>
            ) : categories.filter(c => c.level === 'detail').length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Henüz detay kategori eklenmemiş
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">İsim</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ana Kategori</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alt Kategori</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sıra</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {categories
                    .filter(c => c.level === 'detail')
                    .sort((a, b) => (a.grandParentName || '').localeCompare(b.grandParentName || '') || (a.parentName || '').localeCompare(b.parentName || '') || a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
                    .map(cat => (
                      <tr key={cat._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900">{cat.name}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-teal-100 text-teal-700">
                            {cat.grandParentName || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-cyan-100 text-cyan-700">
                            {cat.parentName || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-500">{cat.sortOrder}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${cat.isActive ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                            {cat.isActive ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleEdit(cat)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            <PencilIcon className="w-5 h-5 inline" />
                          </button>
                          <button
                            onClick={() => handleDelete(cat._id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="w-5 h-5 inline" />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Hiç kategori yoksa bilgi mesajı */}
        {!loading && categories.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-12 mt-6">
            <div className="text-center">
              <FolderIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Henüz Ticimax kategorisi eklenmemiş</p>
              <p className="text-sm text-gray-400 mb-6">
                Yukarıdaki "Excel'den Import" butonu ile toplu kategori ekleyebilir veya 
                her tablo için "Yeni..." butonlarını kullanarak tek tek ekleyebilirsiniz.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Category Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">
              {editingCategory ? 'Kategori Düzenle' : `Yeni ${getLevelLabel(formData.level)}`}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Seviye *</label>
                <select
                  value={formData.level}
                  onChange={(e) => {
                    const newLevel = e.target.value as 'main' | 'sub' | 'detail';
                    setFormData({
                      ...formData, 
                      level: newLevel,
                      parentName: '',
                      grandParentName: ''
                    });
                  }}
                  required
                  disabled={!!editingCategory}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                >
                  <option value="main">Ana Kategori</option>
                  <option value="sub">Alt Kategori</option>
                  <option value="detail">Detay Kategori</option>
                </select>
              </div>

              {/* Ana Kategori Seçimi (sub ve detail için) */}
              {(formData.level === 'sub' || formData.level === 'detail') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ana Kategori *
                  </label>
                  <select
                    value={formData.level === 'sub' ? formData.parentName : formData.grandParentName}
                    onChange={(e) => {
                      if (formData.level === 'sub') {
                        setFormData({...formData, parentName: e.target.value});
                      } else {
                        setFormData({
                          ...formData, 
                          grandParentName: e.target.value,
                          parentName: '' // Alt kategori seçimini sıfırla
                        });
                      }
                    }}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Seçiniz...</option>
                    {mainCategories.map(cat => (
                      <option key={cat._id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Alt Kategori Seçimi (sadece detail için) */}
              {formData.level === 'detail' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alt Kategori *
                  </label>
                  <select
                    value={formData.parentName}
                    onChange={(e) => setFormData({...formData, parentName: e.target.value})}
                    required
                    disabled={!formData.grandParentName}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                  >
                    <option value="">Seçiniz...</option>
                    {filteredSubCategories.map(cat => (
                      <option key={cat._id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                  {formData.grandParentName && filteredSubCategories.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      Bu ana kategoriye ait alt kategori bulunamadı. Önce alt kategori ekleyin.
                    </p>
                  )}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kategori Adı *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="Örn: Kadın Giyim"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sıralama</label>
                <input
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({...formData, sortOrder: parseInt(e.target.value) || 0})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div className="flex gap-3 pt-6">
                <button
                  type="submit"
                  className="flex-1 bg-teal-600 text-white font-semibold py-3 px-4 rounded-xl hover:bg-teal-700 hover:shadow-lg transition-all duration-300"
                >
                  {editingCategory ? 'Güncelle' : 'Kaydet'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCategory(null);
                    resetForm();
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-xl hover:bg-gray-300 transition-colors"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Excel Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">
              Excel'den Kategori Import
            </h3>
            
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-600 mb-2">
                  Excel dosyanız aşağıdaki formatta olmalıdır:
                </p>
                <table className="w-full text-xs border border-gray-200 rounded">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-2 py-1 text-left">Ana Kategori</th>
                      <th className="px-2 py-1 text-left">Alt Kategori</th>
                      <th className="px-2 py-1 text-left">Detay Kategori</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-gray-200">
                      <td className="px-2 py-1">Kadın Giyim</td>
                      <td className="px-2 py-1">Elbise</td>
                      <td className="px-2 py-1">Mini Elbise</td>
                    </tr>
                    <tr className="border-t border-gray-200">
                      <td className="px-2 py-1">Kadın Giyim</td>
                      <td className="px-2 py-1">Elbise</td>
                      <td className="px-2 py-1">Midi Elbise</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Excel Dosyası Seçin
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleExcelImport}
                  disabled={!importFile || importing}
                  className="flex-1 bg-teal-600 text-white font-semibold py-3 px-4 rounded-xl hover:bg-teal-700 hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {importing ? 'Import Ediliyor...' : 'Import Et'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-xl hover:bg-gray-300 transition-colors"
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default TicimaxCategoryManagement;
